import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success(data.message);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-6 noise-bg bg-slate-50" data-testid="forgot-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8">
        <h1 className="font-display text-2xl font-extrabold tracking-tight mb-1">Forgot your password?</h1>
        <p className="text-sm text-slate-500 mb-6">Enter your account email and we will send a reset link (valid for 1 hour).</p>
        {sent ? (
          <p className="text-sm text-green-700" data-testid="forgot-sent">If that email has an account, the reset link is on its way. Check your inbox (and spam folder).</p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="forgot-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button data-testid="forgot-submit" type="submit" disabled={busy || !email} className="w-full bg-slate-900 hover:bg-slate-800">{busy ? 'Sending...' : 'Send reset link'}</Button>
          </form>
        )}
        <button data-testid="forgot-back-login" onClick={() => nav('/login')} className="mt-4 text-xs text-slate-500 underline hover:text-slate-900">Back to sign in</button>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/auth/reset-password', { token, new_password: pw });
      toast.success(data.message);
      nav('/login');
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-6 noise-bg bg-slate-50" data-testid="reset-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8">
        <h1 className="font-display text-2xl font-extrabold tracking-tight mb-1">Set a new password</h1>
        <p className="text-sm text-slate-500 mb-6">All existing sessions will be signed out for safety.</p>
        {!token && <p className="text-sm text-rose-600 mb-3" data-testid="reset-no-token">This link is missing its token. Request a fresh link from the sign-in page.</p>}
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="reset-password-input" type="password" placeholder="New password (8+ chars, letters & numbers)" value={pw} onChange={(e) => setPw(e.target.value)} />
          <Input data-testid="reset-password-confirm" type="password" placeholder="Repeat new password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          <Button data-testid="reset-submit" type="submit" disabled={busy || !token || !pw} className="w-full bg-slate-900 hover:bg-slate-800">{busy ? 'Saving...' : 'Update password'}</Button>
        </form>
      </div>
    </div>
  );
}
